// frontend_pixelmoon/src/pages/admin/AdminPayments.jsx
import React, { useEffect, useState } from "react";
import { getAccessToken } from "../../../utils/authStorage";

export default function AdminPayments() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: "",
    gateway: "",
    dateFrom: "",
    dateTo: "",
  });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  async function fetchData() {
    setLoading(true);
    try {
      const token = getAccessToken();
      const qs = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        status: filters.status,
        gateway: filters.gateway,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      }).toString();

      const url = `/api/v1/admin/payments/orders?${qs}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();

      if (json.success) {
        setData(json.orders || json.data || []);
        setTotal(json.total || json.data?.pagination?.total || 0);
      } else {
        console.error("Fetch error:", json);
        setData([]);
        setTotal(0);
      }
    } catch (err) {
      console.error("fetchData error:", err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    const qs = new URLSearchParams({
      status: filters.status,
      gateway: filters.gateway,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    }).toString();

    const url = `/api/v1/admin/payments/orders/export?${qs}`;

    window.open(url, "_blank");
  }

  const adminAction = async (id) => {
    const newStatus = prompt(
      "Set status (awaiting_payment|paid|processing|completed|failed|resolved|disputed):",
      "paid",
    );
    const note = prompt("Admin note (optional):", "");
    if (!newStatus) return;

    try {
      const token = getAccessToken();
      const base = `/api/v1/admin/payments/orders/${id}`;

      const res = await fetch(base, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, adminNote: note }),
      });

      const json = await res.json();
      if (json.success) {
        alert("Updated successfully");
        fetchData();
      } else {
        alert("Update failed: " + (json.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Admin action error:", err);
      alert("Action error");
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Admin — Payments</h1>

      {/* Tabs + Export */}
      <div className="mb-4 space-x-2">
        <button className="font-bold underline">Orders</button>
        <button
          onClick={exportCsv}
          className="ml-4 px-2 py-1 border rounded bg-gray-100"
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm">Status</label>
          <input
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))
            }
            placeholder="paid / failed"
            className="border p-1 w-full"
          />
        </div>

        <div>
          <label className="block text-sm">Gateway</label>
          <select
            value={filters.gateway}
            onChange={(e) =>
              setFilters((f) => ({ ...f, gateway: e.target.value, page: 1 }))
            }
            className="border p-1 w-full"
          >
            <option value="">All</option>
            <option value="matrixsols">Matrixsols</option>
          </select>
        </div>

        <div>
          <label className="block text-sm">From</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) =>
              setFilters((f) => ({ ...f, dateFrom: e.target.value, page: 1 }))
            }
            className="border p-1 w-full"
          />
        </div>

        <div>
          <label className="block text-sm">To</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) =>
              setFilters((f) => ({ ...f, dateTo: e.target.value, page: 1 }))
            }
            className="border p-1 w-full"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-4 text-center">Loading...</div>
        ) : data.length === 0 ? (
          <div className="p-4 text-center">No records found</div>
        ) : (
          <table className="w-full table-auto border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Gateway</th>
                <th className="p-2 border">Order / Txn</th>
                <th className="p-2 border">User</th>
                <th className="p-2 border">Amount</th>
                <th className="p-2 border">Status</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((it) => (
                <tr key={it._id}>
                  <td className="p-2 border">
                    {new Date(it.createdAt).toLocaleString()}
                  </td>
                  <td className="p-2 border">
                    {it.apiOrder?.provider ||
                      it.paymentMethod ||
                      it.method ||
                      "—"}
                  </td>
                  <td className="p-2 border">{it.orderId}</td>
                  <td className="p-2 border">
                    {it.user?.name ||
                      it.user?.email ||
                      it.userId?.name ||
                      it.userId?.email ||
                      "—"}
                  </td>
                  <td className="p-2 border">
                    ₹{it.paymentInfo?.amount || it.pack?.price || "—"}
                  </td>
                  <td className="p-2 border">{it.status}</td>
                  <td className="p-2 border">
                    <button
                      onClick={() =>
                        window.open(
                          `/admin/payments/orders/${it._id}`,
                          "_blank",
                        )
                      }
                      className="px-2 py-1 border rounded mr-2"
                    >
                      View
                    </button>
                    <button
                      onClick={() => adminAction(it._id)}
                      className="px-2 py-1 border rounded"
                    >
                      Admin Action
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center space-x-4">
        <button
          disabled={filters.page <= 1}
          onClick={() =>
            setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))
          }
          className="px-2 py-1 border rounded"
        >
          Prev
        </button>
        <span>
          Page {filters.page} — Total {total}
        </span>
        <button
          onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
          disabled={filters.page * filters.limit >= total}
          className="px-2 py-1 border rounded"
        >
          Next
        </button>
      </div>
    </div>
  );
}
